import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useAtom } from 'jotai';

import type { GetTemplateCreationRecordResponse } from '../../../../contracts';
import { GET_TEMPLATE_CREATION_RECORD_BY_ID } from '../../../../contracts';
import {
  getTemplateCreationRecordById,
  mergeTemplateCreationRecordUseSections,
} from '../../../../data';
import { getTemplatesDataConfig } from '../../../../data/config';
import { isTemplateDetailsModalOpenAtom } from '../../../template-details/templateDetailsModal.atoms';
import { getTemplateBuilderPorts } from '../../template-builder-ports';
type SelectTemplateCreationRecordResponse<T> = (
  data: GetTemplateCreationRecordResponse
) => T;

const mergeFetchedTemplateCreationRecord = (
  templateCreationRecordId: string,
  incoming: GetTemplateCreationRecordResponse
) => {
  const queryClient = getTemplatesDataConfig().getQueryClient();
  return mergeTemplateCreationRecordUseSections(
    incoming,
    queryClient.getQueryData<GetTemplateCreationRecordResponse>([
      GET_TEMPLATE_CREATION_RECORD_BY_ID,
      { templateCreationRecordId },
    ])
  );
};

export const fetchTemplateCreationRecordById = async (
  templateCreationRecordId: string
) => {
  const queryClient = getTemplatesDataConfig().getQueryClient();
  return await queryClient.fetchQuery({
    queryKey: [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
    queryFn: async () => {
      const { data } = await getTemplateCreationRecordById(templateCreationRecordId);
      return mergeFetchedTemplateCreationRecord(templateCreationRecordId, data);
    },
    staleTime: 0,
  });
};

export function useQueryTemplateCreationRecordById<
  T = GetTemplateCreationRecordResponse,
>({
  templateCreationRecordId,
  select,
}: {
  templateCreationRecordId: string | undefined;
  select: SelectTemplateCreationRecordResponse<T>;
}) {
  const [isTemplateDetailsModalOpen, setIsTemplateDetailsModalOpen] = useAtom(
    isTemplateDetailsModalOpenAtom
  );
  const router = getTemplateBuilderPorts().useRouter();
  const queryClient = getTemplatesDataConfig().getQueryClient();

  return useQuery(
    {
      queryKey: [
        GET_TEMPLATE_CREATION_RECORD_BY_ID,
        { templateCreationRecordId: templateCreationRecordId ?? '' },
      ],
      select,
      queryFn: async () => {
        try {
          const { data } = await getTemplateCreationRecordById(
            templateCreationRecordId ?? ''
          );

          return mergeFetchedTemplateCreationRecord(templateCreationRecordId ?? '', data);
        } catch (error) {
          if (isAxiosError(error) && error.response?.status === 403) {
            if (isTemplateDetailsModalOpen) {
              setIsTemplateDetailsModalOpen(false);
            } else {
              void router.push('/my-templates');
            }
          }
          throw error;
        }
      },
      enabled: !!templateCreationRecordId,
    },
    queryClient
  );
}
